import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '../../lib/api'
import { useAuthStore } from '../auth/auth.store'
import { WISHLIST_ENDPOINTS } from './wishlist.endpoints'
import type { SavedItem, WishlistMessageResponse } from './wishlist.types'

export const useSavedItemsQuery = () => {
  const { customer } = useAuthStore()
  return useQuery({
    queryKey: ['savedItems'],
    queryFn: async () => {
      const { data } = await apiClient.get<SavedItem[]>(WISHLIST_ENDPOINTS.SAVED_ITEMS)
      return data
    },
    enabled: !!customer,
  })
}

export const useToggleWishlistMutation = () => {
  const queryClient = useQueryClient()
  const { customer } = useAuthStore()

  return useMutation({
    mutationFn: async ({ slug, isSaved }: { slug: string; isSaved: boolean }) => {
      if (!customer) throw new Error('Please login to save items')

      if (isSaved) {
        const { data } = await apiClient.delete<WishlistMessageResponse>(WISHLIST_ENDPOINTS.ITEM_DETAIL(slug))
        return data
      }
      const { data } = await apiClient.post<WishlistMessageResponse>(WISHLIST_ENDPOINTS.ITEM_DETAIL(slug))
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['savedItems'] })
      toast.success(data.message)
    },
    onError: (error: any) => {
      const message = error?.message || error?.response?.data?.message || 'Something went wrong'
      toast.error(message)
    },
  })
}

export const useIsWishlisted = (slug: string) => {
  const { data: savedItems } = useSavedItemsQuery()
  return !!savedItems?.find((item) => item.product_slug === slug)
}
